import type { FC } from 'react';

import useTeamCoursesTranslation from '../../hooks/useTeamCoursesTranslation/useTeamCoursesTranslation';
import type { TCertificacionCriteria, TSummary } from '../../types';
import CertificacionCriteria from './CertificacionCriteria';

export type TLegacyCertificationCriteriaProps = {
  summaryCourse: TSummary;
  summaryQuiz: TSummary;
  summaryProject: TSummary;
};

/**
 * Criteria panel as it worked before completion rules existed: three fixed rows
 * derived from the track summary counters (courses, quizzes, projects).
 *
 * Deprecated and deliberately isolated here. It is the safety net for the
 * LX_ADMIN_TRACK_COMPLETION_RULES flag being off; with the flag on, the rule
 * comes from user_track_completion_status and nothing on this file runs. Delete
 * this component together with the flag.
 */
export const LegacyCertificationCriteria: FC<
  TLegacyCertificationCriteriaProps
> = ({ summaryCourse, summaryQuiz, summaryProject }) => {
  const { t } = useTeamCoursesTranslation();

  // A criterion with nothing required is not a criterion: the track has no
  // quizzes/projects to grade, so the row would read "0/0".
  const items: TCertificacionCriteria[] = [
    {
      description: t('LEARN_TEAM_CRITERIA_COURSE_MANDATORY'),
      numberCertifications: {
        completed: summaryCourse.progress,
        expected: summaryCourse.required,
      },
      state: t('LEARN_TEAM_CRITERIA_COMPLETE'),
      typeCertification: t(
        summaryCourse.required > 1
          ? 'LEARN_TEAM_CRITERIA_COURSE_SHORT_plural'
          : 'LEARN_TEAM_CRITERIA_COURSE_SHORT',
      ),
    },
    {
      description: t('LEARN_TEAM_CRITERIA_QUIZ_MANDATORY'),
      numberCertifications: {
        completed: summaryQuiz.progress,
        expected: summaryQuiz.required,
      },
      state: t('LEARN_TEAM_CRITERIA_APPROVES'),
      typeCertification: t(
        summaryQuiz.required > 1
          ? 'LEARN_TEAM_CRITERIA_QUIZ_SHORT_plural'
          : 'LEARN_TEAM_CRITERIA_QUIZ_SHORT',
      ),
    },
    {
      description: t('LEARN_TEAM_CRITERIA_PROJECT_MANDATORY', {
        interpolation: { count: summaryProject.required },
      }),
      numberCertifications: {
        completed: summaryProject.progress,
        expected: summaryProject.required,
      },
      state: t('LEARN_TEAM_CRITERIA_APPROVES'),
      typeCertification: t(
        summaryProject.required > 1
          ? 'LEARN_TEAM_CRITERIA_PROJECT_SHORT_plural'
          : 'LEARN_TEAM_CRITERIA_PROJECT_SHORT',
      ),
    },
  ].filter(item => item.numberCertifications.expected !== 0);

  return (
    <>
      {items.map((item, index) => (
        <CertificacionCriteria
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed list order, index key acceptable here
          key={`certification_criteria_${index * 1}`}
          {...item}
          isBorder={items.length - 1 !== index}
          className="tw-mb-1.2"
        />
      ))}
    </>
  );
};
